import countBy from 'lodash/countBy'
import maxBy from 'lodash/maxBy'
import orderBy from 'lodash/orderBy'
import pick from 'lodash/pick'
import sum from 'lodash/sum'
import { UPLOAD_SRC } from 'components/PhotoBank/PhotoBank.consts'
import type {
  Photo,
  PhotoFaceData,
  PhotoPetData,
  RemotePhoto,
} from 'components/PhotoBank/PhotoBank.types'
import type { AiWallSourcePhoto } from '../aiWall/aiWall.types'

// Weights start from photo-analyzer-v2's docs/pet_photo_selection.md §2–4 (measured on 1000 production orders), minus
// its in_crop bonus (photo-bank photos have no crop). The score only ranks photos: past the detector's confidence floor,
// no signal drops a pet photo, since the best pet beats offering none.

export const PET_SPECIES = ['dog', 'cat'] as const

export type PetSpecies = (typeof PET_SPECIES)[number]

type Pet = PhotoPetData & { species: PetSpecies }

type NormalizedBox = Pick<PhotoPetData, 'x' | 'y' | 'width' | 'height'>

type PetSignals = {
  detectionScore: number
  area: number
  shortSidePx: number
  touchesTop: boolean
  touchedSidesCount: number
  centerDistance: number
  hasOtherPets: boolean
  hasPeople: boolean
}

type ScoredPet = {
  species: PetSpecies
  petBox: NormalizedBox
  signals: PetSignals
  score: number
}

type PhotoPet = ScoredPet & { photo: Photo }

export type PetCandidate = ScoredPet & {
  photo: AiWallSourcePhoto & Pick<RemotePhoto, 'uploadSrc'>
}

const MIN_DETECTION_SCORE = 0.25
// The face model also fires on a pet looking at the camera. That face sits almost entirely inside the pet's box, while a
// person's face cheek to cheek with the pet can still be 70% inside it.
const MIN_FACE_INSIDE_PET_FRACTION = 0.9
const EDGE_MARGIN = 0.02
const FULL_SIZE_PET_AREA = 0.12

export function pickPetCandidate(photos: Photo[]): PetCandidate | null {
  // The first generation is final for the cart, and a photo still waiting for its pets may be the best one.
  if (photos.some(({ petDetection }) => petDetection.status === 'pending')) {
    return null
  }

  const photoPets = photos
    .map(getBestPhotoPet)
    .filter((photoPet) => photoPet !== null)
  if (photoPets.length === 0) return null

  const [{ photo, ...bestPet }] = orderBy(
    getMajoritySpeciesPhotoPets(photoPets),
    [(photoPet) => photoPet.score, (photoPet) => photoPet.signals.shortSidePx],
    ['desc', 'desc']
  )
  // Generation needs the photo's uploaded url, so the best pet waits for its upload rather than losing to an uploaded one.
  if (photo.isLocal) return null
  return {
    ...bestPet,
    photo: pick(photo, ['uid', 'originalUrl', 'width', 'height', 'uploadSrc']),
  }
}

function getMajoritySpeciesPhotoPets(photoPets: PhotoPet[]): PhotoPet[] {
  const speciesCounts = countBy(photoPets, 'species')
  const majorityCount = Math.max(...Object.values(speciesCounts))
  return photoPets.filter(
    (photoPet) => speciesCounts[photoPet.species] === majorityCount
  )
}

function getBestPhotoPet(photo: Photo): PhotoPet | null {
  const { petDetection, faces } = photo
  if (
    isAiGenerationPhoto(photo) ||
    petDetection.status !== 'detected' ||
    !faces
  ) {
    return null
  }

  const confidentPets = getConfidentPets(petDetection.pets)
  // Each pet is scored as the photo's subject, so a second pet costs points rather than the photo.
  const mainPetCandidates = confidentPets.map((mainPet) => {
    const otherPets = confidentPets.filter((pet) => pet !== mainPet)
    const signals = measurePet({ photo, faces, mainPet, otherPets })
    return {
      photo,
      species: mainPet.species,
      petBox: pick(mainPet, ['x', 'y', 'width', 'height']),
      signals,
      score: getPetScore(signals),
    }
  })
  return maxBy(mainPetCandidates, 'score') ?? null
}

// A pet the user already turned into AI art, or an AI result itself, would only repeat that work.
function isAiGenerationPhoto(photo: Photo): boolean {
  return (
    photo.uploadSrc === UPLOAD_SRC.AI_SOURCE ||
    photo.uploadSrc === UPLOAD_SRC.AI_RESULT
  )
}

function getConfidentPets(pets: PhotoPetData[]): Pet[] {
  return pets.flatMap((pet) => {
    const species = PET_SPECIES.find((petSpecies) => petSpecies === pet.label)
    return species && pet.score >= MIN_DETECTION_SCORE
      ? [{ ...pet, species }]
      : []
  })
}

function measurePet({
  photo,
  faces,
  mainPet,
  otherPets,
}: {
  photo: Photo
  faces: PhotoFaceData[]
  mainPet: Pet
  otherPets: Pet[]
}): PetSignals {
  return {
    detectionScore: mainPet.score,
    area: getBoxArea(mainPet),
    shortSidePx: Math.min(
      mainPet.width * photo.width,
      mainPet.height * photo.height
    ),
    touchesTop: mainPet.y < EDGE_MARGIN,
    touchedSidesCount:
      Number(mainPet.x < EDGE_MARGIN) +
      Number(mainPet.x + mainPet.width > 1 - EDGE_MARGIN),
    centerDistance: Math.hypot(
      mainPet.x + mainPet.width / 2 - 0.5,
      mainPet.y + mainPet.height / 2 - 0.5
    ),
    hasOtherPets: otherPets.length > 0,
    hasPeople: faces.some(
      (face) =>
        getFractionOfBoxInside(face, mainPet) < MIN_FACE_INSIDE_PET_FRACTION
    ),
  }
}

function getPetScore(signals: PetSignals): number {
  const scoreParts = {
    confidence: signals.detectionScore,
    size: Math.min(1, signals.area / FULL_SIZE_PET_AREA),
    // Light: a box at the frame's edge is mostly a close-up missing an ear or a paw, which still makes a great source.
    headCut: signals.touchesTop ? -0.05 : 0,
    sidesCut: -0.03 * signals.touchedSidesCount,
    alone: signals.hasOtherPets ? -0.1 : 0.3,
    noPeople: signals.hasPeople ? -0.3 : 0.4,
    resolution:
      signals.shortSidePx >= 800
        ? 0.3
        : signals.shortSidePx >= 400
        ? 0.1
        : -0.5,
    centering: -0.5 * signals.centerDistance,
  }
  return sum(Object.values(scoreParts))
}

function getBoxArea(box: NormalizedBox): number {
  return box.width * box.height
}

function getFractionOfBoxInside(
  box: NormalizedBox,
  container: NormalizedBox
): number {
  const overlapWidth = Math.max(
    0,
    Math.min(box.x + box.width, container.x + container.width) -
      Math.max(box.x, container.x)
  )
  const overlapHeight = Math.max(
    0,
    Math.min(box.y + box.height, container.y + container.height) -
      Math.max(box.y, container.y)
  )
  return (overlapWidth * overlapHeight) / getBoxArea(box)
}
